import axios from 'axios'

/* global __API_URL__ */
const api = axios.create({
  baseURL: __API_URL__,
  timeout: 15000,
})

// Attach JWT to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// Token refresh state — shared across concurrent 401s
let isRefreshing = false
let refreshQueue = []

function processQueue(error, token) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error)
    else resolve(token)
  })
  refreshQueue = []
}

// On 401 → try refreshing the token before giving up
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config

    // Only attempt refresh for 401s, not on login/refresh endpoints, and only once
    if (
      err.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      const refreshToken = localStorage.getItem('refresh_token')

      if (!refreshToken) {
        // No refresh token — force logout
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login'
        }
        return Promise.reject(err)
      }

      if (isRefreshing) {
        // Another refresh is in progress — queue this request
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject })
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return api(originalRequest)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const { data } = await axios.post(
          `${__API_URL__}/auth/refresh`,
          null,
          { params: { refresh_token: refreshToken } }
        )

        const newAccessToken = data.access_token
        const newRefreshToken = data.refresh_token

        localStorage.setItem('access_token', newAccessToken)
        if (newRefreshToken) localStorage.setItem('refresh_token', newRefreshToken)

        // Update Zustand store token (without importing useAuth to avoid circular deps)
        try {
          const stored = JSON.parse(localStorage.getItem('crypto-auth') || '{}')
          if (stored.state) {
            stored.state.token = newAccessToken
            localStorage.setItem('crypto-auth', JSON.stringify(stored))
          }
        } catch {}

        processQueue(null, newAccessToken)

        // Retry the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return api(originalRequest)
      } catch (refreshErr) {
        processQueue(refreshErr, null)
        // Refresh failed — force logout
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        try {
          const stored = JSON.parse(localStorage.getItem('crypto-auth') || '{}')
          if (stored.state) {
            stored.state.token = null
            stored.state.user = null
            localStorage.setItem('crypto-auth', JSON.stringify(stored))
          }
        } catch {}
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login'
        }
        return Promise.reject(refreshErr)
      } finally {
        isRefreshing = false
      }
    }

    // Non-401 errors or login endpoint — just reject
    if (err.response?.status === 401) {
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      // Only redirect if not already on a public page
      const pub = ['/login', '/register', '/pricing', '/terms', '/privacy', '/contact', '/']
      if (!pub.some(p => window.location.pathname === p)) {
        window.location.href = '/login'
      }
    }
    return Promise.reject(err)
  }
)

export default api
